import React from 'react'
import type { BettingType, Selection, BetSpecialOffer } from '@mobi/betslip/types'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors } from '@mobi/component-library/Foundations'
import { createBetSlipItem } from '@mobi/betslip/helpers/createBetSlipItem'
import { doesClientSupportShare } from '@core/Areas/ShareBet/helpers/doesClientSupportShare'
import { openShareOutgoing } from '@core/Areas/ShareBet/ShareBetOutgoing/helpers/emitter'
import { state$ as quickbetState$, QuickbetStateRecord } from '@core/Areas/Quickbet/driver'
import { QuickbetReceiptState } from '@core/Areas/Quickbet/Components/Receipt/driver'
import { observeImmutable } from '@core/Components/HOCs/observe'
import { convertToBetSelection } from '@core/Utils/betting/handleBetSelection/helpers/convertToBetSelection'
import { ReceiptButtonStyled } from '@core/Areas/Quickbet/Components/Receipt/Components/Common/Receipt.styles'
import {
  ShareToBetLoopTooltip,
  useShareToBetLoopTooltip,
} from '@core/Areas/BetLoop/hooks/useShareToBetLoopTooltip'
import { shouldAllowShareInBetLoop } from '@core/Areas/BetLoop/helpers/shouldAllowShareInBetLoop'

const enum LocalConstants {
  ShareButtonSeenCountStorageKey = 'betloop:quickbet-share-tooltip-count',
}

type Props = Pick<
  QuickbetReceiptState,
  'winInvestment' | 'placeInvestment' | 'bonusBet' | 'betCost' | 'ticketNumber'
> & {
  specialOffers: BetSpecialOffer[]
  hasSpecialOffer: boolean
}

export const ShareButton: React.FC<Props> = ({
  winInvestment,
  placeInvestment,
  bonusBet,
  betCost,
  ticketNumber,
  specialOffers,
  hasSpecialOffer,
}) => {
  if (!doesClientSupportShare()) return null

  return (
    <ShareButtonComponent
      winInvestment={winInvestment}
      placeInvestment={placeInvestment}
      bonusBet={bonusBet}
      ticketNumber={ticketNumber}
      betCost={betCost}
      specialOffers={specialOffers}
      hasSpecialOffer={hasSpecialOffer}
    />
  )
}

// ===============
// Local Component
// ===============

const ShareButtonComponent = observeImmutable<QuickbetStateRecord, Props>(
  quickbetState$,
  ({
    record: quickbet,
    winInvestment,
    placeInvestment,
    bonusBet,
    ticketNumber,
    betCost,
    specialOffers,
    hasSpecialOffer,
  }) => {
    const quickbetState = quickbet.toJS()

    const {
      bettingType,
      selection,
      selectionDetails,
      isEachWayAvailable,
      shouldAllowPlaceInvestment,
      betSource,
    } = quickbetState

    const { shareButtonRef, onShareButtonClick, handleDismissShareTooltip, isShareTooltipVisible } =
      useShareToBetLoopTooltip(LocalConstants.ShareButtonSeenCountStorageKey)

    const item = React.useMemo(
      () =>
        createBetSlipItem(
          convertToBetSelection({
            bettingType: bettingType as BettingType,
            selection: selection as Selection,
            selectionDetails,
            isEachWayAvailable,
            shouldAllowPlaceInvestment,
            betSource,
          })
        ),
      [
        bettingType,
        selection,
        selectionDetails,
        isEachWayAvailable,
        shouldAllowPlaceInvestment,
        betSource,
      ]
    )

    const canShareToBetloop = React.useMemo(
      () =>
        shouldAllowShareInBetLoop({
          contextType: 'quickbet',
          share: { items: [item], ticketSequence: ticketNumber as number, betCost },
        }),
      [item, ticketNumber, betCost]
    )

    const handleShareBetClick = () => {
      onShareButtonClick()

      const shouldIncludeInvestment = !!(winInvestment || placeInvestment)
      if (shouldIncludeInvestment) {
        item.investment = {
          win: { value: winInvestment ?? 0 },
          place: { value: placeInvestment ?? 0 },
          bonusBet: { campaignId: 0, value: bonusBet ?? 0 },
        }
      }

      if (hasSpecialOffer) {
        item.selectedSuperPickOffer = specialOffers[0] || null
        item.specialOffers = specialOffers
      }

      openShareOutgoing({
        contextType: 'quickbet',
        betSource,
        share: { items: [item], ticketSequence: ticketNumber as number, betCost },
      })
    }

    return (
      <>
        <ReceiptButtonStyled
          onClick={handleShareBetClick}
          color='secondary'
          type='button'
          role='button'
          ref={shareButtonRef}
        >
          <Icon name='LineShare02' color={colors.black} /> Share My Bet
        </ReceiptButtonStyled>

        {isShareTooltipVisible && canShareToBetloop && (
          <ShareToBetLoopTooltip
            triggerElRef={shareButtonRef}
            onDismissed={handleDismissShareTooltip}
          />
        )}
      </>
    )
  }
)
